import { Input, Select, SubmitButton } from '@panwds/react-form';
import { Button, Link, SelectItem, useModal } from '@panwds/react-ui';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslate } from "../../customHooks";
import { isEmpty } from 'lodash';
import { toast } from '../../components';
import styles from './styles/Register.module.scss';
import { useCallback, useEffect, useState } from 'react';
import { useLazyGetSubscriptionsQuery } from '../../redux/services/subscriptions-service';
import AppConfig from '../../config.json';
import { oktaProvider } from '../../authProvider';
import jwt_decode from "jwt-decode";
import { useAssociateBillingAccessCodeMutation } from '../../redux/services/scm-billing-association-service';
import { AssociateBillingAccessCodeResponse, CreateLinkAccountRequest } from '../../interfaces/interfaces';
import { useCreateLinkAccountMutation } from '../../redux/services/accounts-service';
import { SUBSCRIPTION_ACCOUNT_STATUS } from '../../utils/constants';
import { RouteUri } from '../../routeUri';
import { useHistory } from 'react-router-dom';

function isJwtExpired(token) {
    if (!token) return true;

    try {
        const decoded = jwt_decode(token);

        if (!decoded.exp) return true;

        const now = Math.floor(Date.now() / 1000); // current time in seconds
        return decoded.exp < now;
    } catch (e) {
        toast.error(e?.error, { toastId: "subscription-link-account-token" });
        return true;
    }
}

export const SubscriptionLinkAccount = (props: any) => {
    const translate = useTranslate();
    const history = useHistory();
    const { tokens, queryString } = props?.state;
    const [tenantChoices, setTenantChoices] = useState<
        SelectItem[] | undefined
    >([]);
    const issuerIdpO = tokens?.idToken?.issuer === AppConfig?.oidc?.issuer
    const BILLING_ASSOCIATION_CODE = queryString['BillingAssociationCode'];
    const ACCOUNT_STATUS = queryString["link-state"];
    const [nextStep, setNextStep] = useState<{ TsgId: string } | { AwsAccountId: string } | undefined>()

    const [triggerSubscriptions, { data: subscriptions, error, isFetching: subscriptionsLoading }] = useLazyGetSubscriptionsQuery();
    const [triggerAssociateBillingAccessCode, { error: billingAssociationFailed, isLoading: associatingBillingCode }] = useAssociateBillingAccessCodeMutation();
    const [triggerCreateLinkAccount, { error: linkAccountFailed, isLoading: creatingLinkAccount }] = useCreateLinkAccountMutation();

    const formMethods = useForm({});
    const {
        formState: { isValid }
    } = formMethods

    useEffect(() => {
        if (queryString && !isJwtExpired(tokens?.idToken?.idToken)) {
            triggerSubscriptions({
                payload: {
                    LoginType: issuerIdpO ? "IdpO" : "IdpC",
                    AccountId: queryString['acc-id']
                },
                headers: {
                    Authorization: tokens?.idToken?.idToken,
                    'Content-Type': 'application/json',
                },
            });
        }
    }, [tokens, issuerIdpO, queryString]);

    useEffect(() => {
        if (!isEmpty(subscriptions)) {
            if (subscriptions?.length === 1) {
                //Mimic the handleSubmit of the form when there are more than 1 tenants
                handleLinkAccountCreate({
                    TenantId: subscriptions[0]?.SubscriptionKey,
                    BillingAssociationCode: BILLING_ASSOCIATION_CODE
                });
            }
            setTenantChoices(
                subscriptions?.map((data: any) => ({
                    children: (data?.CspSerialNumber) ? `${data?.LinkAccountId} (CSP: ${data?.CspSerialNumber})` : data?.LinkAccountId,
                    value: data?.SubscriptionKey,
                    context: {
                        TenantVersion: data?.TenantVersion
                    },
                }))
            );
        }
        else {
            setTenantChoices(undefined);
        }
    }, [subscriptions, BILLING_ASSOCIATION_CODE]);

    useEffect(() => {
        if (error) {
            //toast.error(error?.error, { toastId: "list-subscriptions" });
            setTenantChoices(undefined);
        }
    }, [error]);


    /**
 * Associates a billing access code with the user's account
 * @param params The parameters containing user email, billing code, AWS account ID, and tenant ID
 * @param tokens The authentication tokens
 * @param openModalFn Function to open the success modal
 * @returns Result of the association operation
 */
    const associateBillingAccessCode = useCallback(async (email: string, awsAccountId: string, tenant: string) => {
        try {
            const billingCodeAssociated: AssociateBillingAccessCodeResponse = await triggerAssociateBillingAccessCode({
                payload: {
                    UserEmail: email,
                    BillingAccessCode: BILLING_ASSOCIATION_CODE,
                    AWSAccountID: awsAccountId,
                },
                headers: {
                    'Authorization': tokens?.idToken?.idToken,
                    'x-api-key': tenant,
                },
            }).unwrap();

            if (billingCodeAssociated?.TsgID) {
                setNextStep({ TsgId: billingCodeAssociated?.TsgID });
            }
        } catch (err) {
            toast.error(err?.error, { toastId: "associate-scm-code" });
        }

    }, []);

    const handleLinkAccountCreate = async (params: any = {}) => {
        const AWSMarketplaceToken = queryString["x-amzn-marketplace-token"] || "";
        const ProductId = queryString["x-amzn-marketplace-product-id"] || "";
        const AgreementId = queryString["x-amzn-marketplace-agreement-id"] || "";
        const AccoutId = queryString["acc-id"] || ""
        const Tenant = params?.TenantId || tenantChoices?.[0]?.value;

        const createAccountPayload: CreateLinkAccountRequest = {
            Origin: "AwsMarketplace",
            AWSMarketplaceToken: AWSMarketplaceToken,
            ProductId: ProductId,
            AgreementId: AgreementId,
            AccountId: AccoutId
        };

        if (params.BillingAssociationCode) {
            const decoded = jwt_decode(tokens?.idToken?.idToken);
            try {
                if (ACCOUNT_STATUS === SUBSCRIPTION_ACCOUNT_STATUS.LINKED ||
                    ACCOUNT_STATUS === SUBSCRIPTION_ACCOUNT_STATUS.IN_METERING_GROUP) {
                    associateBillingAccessCode(decoded?.email, AccoutId, Tenant);
                } else {
                    const accountCreated = await triggerCreateLinkAccount({
                        payload: createAccountPayload,
                        headers: {
                            Authorization: tokens?.idToken?.idToken,
                            'x-api-key': Tenant,
                            'Content-Type': 'application/json',
                        },
                    }).unwrap();
                    if (accountCreated?.AccountId) {
                        associateBillingAccessCode(decoded?.email, accountCreated?.AccountId, Tenant);
                    }
                }
            } catch (err) {
                //toast.error(err?.error, { toastId: "validate-associate-scm-code" });
            }
        } else {
            try {
                const accountCreated = await triggerCreateLinkAccount({
                    payload: createAccountPayload,
                    headers: {
                        Authorization: tokens?.idToken?.idToken,
                        'x-api-key': Tenant,
                        'Content-Type': 'application/json',
                    },
                }).unwrap();
                if (accountCreated?.AccountId) {
                    setNextStep({ AwsAccountId: accountCreated?.AccountId });
                }
            } catch (err) {
                setNextStep({ AwsAccountId: AccoutId });
                //toast.error(err?.error, { toastId: "crete-subscription-link-account" });
            }
        }
    };

    return (
        <>
            {(linkAccountFailed || billingAssociationFailed) ?
                (<>
                    <div className='formLayout'>
                        <div className='tw-p-4'>
                            <b><span className="tw-text-red-500">{translate('generic.error')}</span></b>
                            <span className="tw-text-sm tw-mt-2 tw-p-2">
                                {linkAccountFailed?.error || billingAssociationFailed?.error || translate('generic.requestFailed')}
                            </span>
                        </div>
                    </div>
                </>) : nextStep && 'TsgId' in nextStep ?
                    (<>
                        <div className='tw-p-4'>
                            <div className='formLayout'>
                                <b><span className="tw-text-sm">{
                                    translate("scmBillingCodeAssociation.subTitle")}
                                </span></b>
                                <span className="tw-text-sm tw-mb-2">{
                                    translate("scmBillingCodeAssociation.description")}
                                </span>
                            </div>
                            <Button
                                addClassName={`${styles.customButton} tw-mt-4`}
                                size="lg"
                                data-metrics="cloudngfw-sign-up-scm-code-associated"
                                type="submit"
                            >
                                <Link
                                    target="_blank"
                                    href={`${import.meta.env.VITE_SCM_URL}?${nextStep?.TsgId}`}
                                    external
                                >
                                    {translate("scmBillingCodeAssociation.submitButton")}
                                </Link>
                            </Button>
                        </div>
                    </>) :
                    nextStep && 'AwsAccountId' in nextStep ?
                        (<>
                            <div className='tw-p-4'>
                                <div className='formLayout'>
                                    <b><span className="tw-text-sm">{
                                        translate("linkTenantSuccessful.subTitle")}
                                    </span></b>
                                    <span className="tw-text-sm tw-mb-2">{
                                        translate("linkTenantSuccessful.description")}
                                    </span>
                                </div>
                                <Button
                                    addClassName={`${styles.customButton} tw-mt-4`}
                                    size="lg"
                                    data-metrics="cloudngfw-sign-up-scm-code-associated"
                                    type="submit"
                                    onClick={
                                        () => {
                                            history.replace(RouteUri.Login);
                                        }
                                    }
                                >
                                    {translate("generic.login")}
                                </Button>
                            </div>
                        </>) : subscriptionsLoading ?
                            (<>
                                <div className='formLayout'>
                                    <div className='tw-p-4'>
                                        <span>{translate('generic.loading')}</span>
                                    </div>
                                </div>
                            </>) :
                            (creatingLinkAccount || associatingBillingCode) ?
                                (<>
                                    <div className='formLayout'>
                                        <b><span className="tw-text-sm">{
                                            translate("linkAccount.inProgress")}
                                        </span></b>
                                        <span className="tw-text-sm tw-mb-2">{
                                            translate("linkAccount.inProgress")}
                                        </span>
                                    </div>
                                </>) :
                                (
                                    (isJwtExpired(tokens?.idToken?.idToken)) ? (
                                        <div className="formLayout tw-p-4">
                                            <div>
                                                <span className="tw-text-sm">{translate(`noTenant.expiredSession`)}</span>
                                            </div>
                                            <div className='tw-pt-4'>
                                                <Button addClassName={styles.customButton} onClick={() => {
                                                    const options = {
                                                        state: queryString
                                                    };
                                                    oktaProvider.signIn(options);
                                                }}>Login</Button>
                                            </div>
                                        </div>
                                    )
                                        : (tenantChoices === undefined && !subscriptionsLoading) ? (
                                            <div className="formLayout">
                                                <div>
                                                    <div>
                                                        <b><span className="tw-text-sm">{translate(`noTenant.subTitle`)}</span></b>
                                                        <span className={`${styles.subTitle} tw-pt-2 tw-text-sm`}>{translate(`noTenant.description`)}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (tenantChoices && tenantChoices.length > 1) && (
                                            <FormProvider {...formMethods}>
                                                <form onSubmit={formMethods.handleSubmit(handleLinkAccountCreate)}>
                                                    <div className="formLayout">
                                                        <Input
                                                            name="AccountId"
                                                            label={translate("generic.awsAccountId")}
                                                            dataMetrics="cloudngfw-link-account-accountId"
                                                            requiredAsterisk
                                                            value={queryString['acc-id']}
                                                            readOnly
                                                        />
                                                        {(tenantChoices?.length && tenantChoices?.length > 1) &&
                                                            <Select
                                                                items={tenantChoices}
                                                                label={translate("generic.tenantId")}
                                                                name="TenantId"
                                                                dataMetrics="cloudngfw-link-account-tenant"
                                                                requiredAsterisk
                                                                simpleFormat
                                                                isLoading={subscriptionsLoading}
                                                            />
                                                        }

                                                        {BILLING_ASSOCIATION_CODE &&
                                                            <Input
                                                                name="BillingAssociationCode"
                                                                label={translate("auth.billingAssociationCode")}
                                                                dataMetrics="cloudngfw-link-account-association-code"
                                                                requiredAsterisk
                                                                value={BILLING_ASSOCIATION_CODE}
                                                                readOnly
                                                            />
                                                        }

                                                        <SubmitButton addClassName={styles.customButton} disabled={!isValid}>{translate(`generic.continue`)}</SubmitButton>
                                                    </div>
                                                </form>
                                            </FormProvider>
                                        )
                                )}
        </>
    );
};
